import { Row, Col } from "reactstrap";
import { useEffect, useMemo, useState } from "react";
import TopCardsData from "./TopCardsData";
import icon1 from "../../assets/images/dashboard/icons/income.png";
import icon2 from "../../assets/images/dashboard/icons/expense.png";
import icon3 from "../../assets/images/dashboard/icons/assets.png";
import icon4 from "../../assets/images/dashboard/icons/staff.png";

import { getUserCreditsSummaryAPI } from "../../service/ManageUserCreditsService";
import { getUser } from "../../service/AuthService";

function formatDateMDY(iso) {
  if (!iso) return "--";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "--";
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  const yyyy = d.getUTCFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

// Get today's date as yyyy-mm-dd (UTC-based)
function todayYMD() {
  const now = new Date();
  const yyyy = now.getUTCFullYear();
  const mm = String(now.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(now.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

// Compare expiry date string 'yyyy-mm-dd' vs today
function isExpired(expiryYMD) {
  if (!expiryYMD) return false;
  const today = todayYMD();
  // ISO yyyy-mm-dd strings are lexicographically comparable
  return expiryYMD < today;
}

const TopCards = ({ onBalanceLoaded }) => {
  const rawUser = getUser();
  const userSafe = useMemo(() => {
    if (!rawUser || rawUser === "undefined") return null;
    return rawUser;
  }, [rawUser]);

  const orgName = userSafe?.organization?.trim() || "";
  const username = userSafe?.username?.trim() || "";

  const [total, setTotal] = useState(0);
  const [used, setUsed] = useState(0);
  const [balance, setBalance] = useState(0);
  const [expiry, setExpiry] = useState("");
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchCredits() {
      if (!orgName || !username) {
        if (!cancelled) {
          setTotal(0);
          setUsed(0);
          setBalance(0);
          setExpiry("");
          setExpired(false);
          onBalanceLoaded?.(0);
        }
        return;
      }

      try {
        const resp = await getUserCreditsSummaryAPI({
          action: "getUserCreditsSummary",
          organization: orgName,
          username,
        });

        const row = resp?.item || null; // expect single row only
        const available = Number(row?.available_credits || 0);
        const rawTotal = Number(row?.total_credits);
        // Fallback: if total_credits missing/NaN, use available as total
        const totalCredits =
          Number.isFinite(rawTotal) && rawTotal >= 0 ? rawTotal : available;

        const end = row?.credits_expiry_date || "";
        const expiredFlag = isExpired(end);

        if (cancelled) return;

        const usedCredits = Math.max(0, totalCredits - available);

        setBalance(available);
        setTotal(totalCredits);
        setUsed(usedCredits);
        setExpiry(end);
        setExpired(expiredFlag);

        // Keep callback behavior same (you already pass balance + expiry out)
        onBalanceLoaded?.(available, end);
      } catch {
        if (!cancelled) {
          setTotal(0);
          setUsed(0);
          setBalance(0);
          setExpiry("");
          setExpired(false);
          onBalanceLoaded?.(0);
        }
      }
    }

    fetchCredits();
    return () => {
      cancelled = true;
    };
  }, [orgName, username, onBalanceLoaded]);

  const expiryLabel = expired
    ? `${formatDateMDY(expiry)} (Expired)`
    : formatDateMDY(expiry);

  return (
    <Row>
      <Col sm="6" lg="3">
        <TopCardsData
          img={icon1}
          title={total}
          subtitle="Total Credits"
          disabled={expired}   // 👈 dim/disable when expired
        />
      </Col>
      <Col sm="6" lg="3">
        <TopCardsData
          img={icon2}
          title={used}
          subtitle="Credits Used"
          disabled={expired}
        />
      </Col>
      <Col sm="6" lg="3">
        <TopCardsData
          img={icon3}
          title={balance}
          subtitle="Credit Balance"
          disabled={expired}
        />
      </Col>
      <Col sm="6" lg="3">
        <TopCardsData
          img={icon4}
          title={formatDateMDY(expiry)}
          subtitle={expired ? "Expired On (UTC)" : "Expires On (UTC)"}
          subtitleColor={expired ? "#C00000" : undefined}
          disabled={expired}
        />
      </Col>
    </Row>
  );
};

export default TopCards;
